import React from "react";
import { RotateCcw } from "lucide-react";
import { Branch, Chips, TreeHeader } from "./FaultTreePrimitives";

/**
 * Performance / Power fault tree — DEEP, 6 branching levels.
 *
 *   1. Primary symptom (loss of power / hesitation / rough idle / poor economy / smoke / no start)
 *   2. When does it happen? (cold / hot / under load / at idle / always)
 *   3. Smoke colour (none / white / blue / black)
 *   4. Limp mode (yes / no / unsure)
 *   5. Recent triggers (after fuel fill / after repair / after software update / none)
 *   6. Notes — customer's own words
 */
export default function PerformanceFaultTree({
  data = {},
  onChange = () => {},
  onReset,
}) {
  const showWhen = !!data.primary;
  const showSmoke =
    data.primary === "smoke" ||
    data.primary === "loss_of_power" ||
    data.primary === "rough_idle";
  const showLimp = !!data.primary;
  const showTrigger = !!data.primary;
  const showEconomy = data.primary === "poor_economy";
  const showNotes = !!data.primary;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="performance-fault-tree"
    >
      <TreeHeader
        eyebrow="Diagnostic · Fault tree"
        title="Performance / Power triage"
        tone="amber"
        right={
          onReset && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-800"
              data-testid="perf-reset"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )
        }
      />

      <div className="p-4 space-y-3.5">
        <Branch step={1} label="Primary symptom" testId="branch-perf-primary">
          <Chips
            tone="amber"
            value={data.primary}
            options={[
              { v: "loss_of_power", label: "Loss of power" },
              { v: "hesitation", label: "Hesitation / stumble" },
              { v: "rough_idle", label: "Rough idle / misfire" },
              { v: "poor_economy", label: "Poor fuel economy" },
              { v: "smoke", label: "Smoke from exhaust" },
              { v: "no_start", label: "Won’t start / slow to start" },
              { v: "stalling", label: "Stalls" },
            ]}
            onChange={(v) =>
              onChange({
                primary: v,
                when: null,
                smoke_colour: null,
                limp_mode: null,
                recent_trigger: null,
              })
            }
            testId="perf-primary"
          />
        </Branch>

        {showWhen && (
          <Branch step={2} label="When does it happen?" testId="branch-perf-when">
            <Chips
              tone="amber"
              multi
              value={data.when || []}
              options={[
                { v: "cold_start", label: "From cold" },
                { v: "hot", label: "When hot" },
                { v: "under_load", label: "Under load / acceleration" },
                { v: "at_idle", label: "At idle" },
                { v: "cruising", label: "While cruising" },
                { v: "stop_start", label: "Stop-start" },
                { v: "always", label: "Always" },
              ]}
              onChange={(arr) => onChange({ when: arr })}
              testId="perf-when"
            />
          </Branch>
        )}

        {showSmoke && (
          <Branch
            step={3}
            label="Smoke colour"
            subtitle="Smoke colour points sharply to the system at fault."
            testId="branch-perf-smoke"
          >
            <Chips
              tone="amber"
              value={data.smoke_colour}
              options={[
                { v: "none", label: "None / not noticed" },
                { v: "white", label: "White (coolant / steam)" },
                { v: "blue", label: "Blue (oil)" },
                { v: "black", label: "Black (rich fuel)" },
                { v: "grey", label: "Grey" },
              ]}
              onChange={(v) => onChange({ smoke_colour: v })}
              testId="perf-smoke"
            />
          </Branch>
        )}

        {showLimp && (
          <Branch step={4} label="Has it gone into limp mode?" testId="branch-perf-limp">
            <Chips
              tone="amber"
              value={data.limp_mode}
              options={[
                { v: "yes", label: "Yes — restricted" },
                { v: "no", label: "No" },
                { v: "unsure", label: "Not sure" },
              ]}
              onChange={(v) => onChange({ limp_mode: v })}
              testId="perf-limp"
            />
          </Branch>
        )}

        {showEconomy && (
          <Branch
            step={5}
            label="Economy change vs normal"
            subtitle="Rough estimate is fine — sharp drops are diagnostic."
            testId="branch-perf-economy"
          >
            <Chips
              tone="amber"
              value={data.economy_change}
              options={[
                { v: "slight", label: "Slight drop" },
                { v: "noticeable", label: "Noticeable drop" },
                { v: "significant", label: "Very significant" },
                { v: "unsure", label: "Not sure" },
              ]}
              onChange={(v) => onChange({ economy_change: v })}
              testId="perf-economy"
            />
          </Branch>
        )}

        {showTrigger && (
          <Branch step={6} label="Recent trigger" testId="branch-perf-trigger">
            <Chips
              tone="amber"
              value={data.recent_trigger}
              options={[
                { v: "after_fuel_fill", label: "After a fuel fill" },
                { v: "after_repair", label: "After a recent repair" },
                { v: "after_software", label: "After a software update / recall" },
                { v: "after_long_drive", label: "After a long drive" },
                { v: "none", label: "Nothing in particular" },
              ]}
              onChange={(v) => onChange({ recent_trigger: v })}
              testId="perf-trigger"
            />
          </Branch>
        )}

        {showNotes && (
          <Branch step={7} label="Customer’s own words" testId="branch-perf-notes">
            <textarea
              rows={2}
              value={data.notes || ""}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="e.g. ‘felt like it was running out of fuel uphill on the motorway’"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-amber-500"
              data-testid="perf-notes"
            />
          </Branch>
        )}
      </div>
    </div>
  );
}
